<template>
  <div>
    <div class="recurrency-card">
      <div class="recurrency-header">
        <div class="recurrency-avatar recurrency-avatar-placeholder">
          <i class="fa-solid fa-repeat"></i>
        </div>
        <div class="recurrency-header-info">
          <div class="recurrency-name">Recorrência</div>
          <div class="recurrency-price">R$ {{ recurrency.value }}</div>
        </div>
        <span
          class="recurrency-status-badge"
          :class="{
            'status-pending': recurrency.status === 'pending',
            'status-active': recurrency.status === 'active',
            'status-cancelled': !['pending', 'active'].includes(recurrency.status),
          }"
        >
          <i
            class="fa-solid me-1"
            :class="{
              'fa-circle-exclamation': recurrency.status === 'pending',
              'fa-circle-check': recurrency.status === 'active',
              'fa-circle-xmark': !['pending', 'active'].includes(recurrency.status),
            }"
          ></i>
          <span v-if="recurrency.status === 'pending'">Pendente</span>
          <span v-else-if="recurrency.status === 'active'">Ativa</span>
          <span v-else>Cancelada</span>
        </span>
      </div>

      <div class="recurrency-body">
        <div class="recurrency-info-line">
          <i class="fa-solid fa-calendar-days me-2 text-muted"></i>
          <span>
            <strong>{{ frequencyLabel }}</strong>
            <span v-if="dayLabel" class="text-muted"> · {{ dayLabel }}</span>
          </span>
        </div>
        <div v-if="showPatient && recurrency.user" class="recurrency-info-line">
          <i class="fa-solid fa-user me-2 text-muted"></i>
          <span>
            <span class="text-muted">Paciente:</span>
            <router-link
              v-if="recurrency.user.id"
              :to="`${$paths.users.detail}/${recurrency.user.id}`"
              class="ms-1 recurrency-name-link"
            >
              {{ recurrency.user.name }}
            </router-link>
            <span v-else class="ms-1">{{ recurrency.user.name }}</span>
          </span>
        </div>
        <div
          v-if="showPsychologist && recurrency.psychologist"
          class="recurrency-info-line"
        >
          <i class="fa-solid fa-user-doctor me-2 text-muted"></i>
          <span>
            <span class="text-muted">Psi:</span>
            <router-link
              v-if="recurrency.psychologist.id"
              :to="`${$paths.psychologists.detail}/${recurrency.psychologist.id}`"
              class="ms-1 recurrency-name-link"
            >
              {{ recurrency.psychologist.name }}
            </router-link>
            <span v-else class="ms-1">{{ recurrency.psychologist.name }}</span>
          </span>
        </div>
        <div v-if="hasPendingValueChange" class="recurrency-value-change">
          <i class="fa-solid fa-arrow-trend-up me-2"></i>
          <span>
            Mudança de valor agendada para
            <strong>
              {{ $moment(recurrency.value_change_effective_at).format("DD/MM/YYYY") }}
            </strong>
          </span>
        </div>
        <div v-if="recurrency.created_at" class="recurrency-info-line text-muted">
          <i class="fa-solid fa-clock me-2"></i>
          <small>
            Recorrência criada em
            {{ $moment(recurrency.created_at).format("DD/MM/YYYY") }}
          </small>
        </div>
      </div>

      <div class="recurrency-footer">
        <router-link
          :to="{
            path: $paths.appointments.list,
            query: { recurrency_id: recurrency.id },
          }"
          class="btn btn-outline-secondary btn-sm flex-fill"
        >
          <i class="fa-solid fa-calendar me-1"></i>
          Ver sessões
        </router-link>
        <template v-if="recurrency.status === 'active'">
          <button
            class="btn btn-outline-primary btn-sm flex-fill"
            @click="editing = recurrency"
          >
            <i class="fa-solid fa-pen me-1"></i>
            Editar
          </button>
          <button
            class="btn btn-outline-danger btn-sm flex-fill"
            :disabled="canceling"
            @click="handleCancel"
          >
            <span v-if="canceling">
              <i class="fa-solid fa-spinner fa-spin me-1"></i>
              Cancelando...
            </span>
            <span v-else>
              <i class="fa-solid fa-ban me-1"></i>
              Cancelar
            </span>
          </button>
        </template>
      </div>
    </div>

    <RecurrencyEditModal
      :recurrency="editing"
      @close="editing = null"
      @saved="onEditSaved"
    />
  </div>
</template>

<script>
export default {
  name: "RecurrencyCard",
  props: {
    recurrency: {
      type: Object,
      required: true,
    },
    showPatient: {
      type: Boolean,
      default: true,
    },
    showPsychologist: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      canceling: false,
      editing: null,
    };
  },
  computed: {
    frequencyLabel() {
      const r = this.recurrency;
      if (!r) return "";
      if (r.frequency === "weekly" && r.interval === 1) return "Semanal";
      if (r.frequency === "weekly" && r.interval === 2) return "Quinzenal";
      if (r.frequency === "monthly" && r.interval === 1) return "Mensal";
      return `${r.frequency} (intervalo ${r.interval})`;
    },
    dayLabel() {
      if (!this.recurrency?.reference_date) return "";
      const ref = this.$moment(this.recurrency.reference_date);
      if (!ref.isValid()) return "";
      const minutes = ref.minute();
      const time =
        minutes === 0
          ? `${ref.hour()}h`
          : `${ref.hour()}h${String(minutes).padStart(2, "0")}`;
      if (this.recurrency.frequency === "weekly") {
        const weekdays = [
          "domingo",
          "segunda-feira",
          "terça-feira",
          "quarta-feira",
          "quinta-feira",
          "sexta-feira",
          "sábado",
        ];
        return `${weekdays[ref.day()]} às ${time}`;
      }
      if (this.recurrency.frequency === "monthly") {
        return `dia ${ref.date()} às ${time}`;
      }
      return "";
    },
    hasPendingValueChange() {
      if (!this.recurrency?.value_change_effective_at) return false;
      return this.$moment(this.recurrency.value_change_effective_at).isAfter(
        this.$moment()
      );
    },
  },
  methods: {
    onEditSaved() {
      this.editing = null;
      this.$emit("refresh");
    },
    async handleCancel() {
      const ok = confirm(
        "Tem certeza que deseja cancelar esta recorrência?\n\nSomente sessões futuras que NÃO foram pagas serão canceladas."
      );
      if (!ok) return;
      this.canceling = true;
      try {
        await this.$appointmentsService.cancelRecurrency(this.recurrency.id);
        this.$eventBus.$emit("showToast", {
          title: "Recorrência cancelada",
          msg: "Sessões futuras que não haviam sido pagas foram canceladas.",
        });
        this.$emit("refresh");
      } catch (error) {
        this.$eventBus.$emit("showToast", {
          title: "Erro",
          msg: error.message || "Erro ao cancelar recorrência.",
        });
      } finally {
        this.canceling = false;
      }
    },
  },
};
</script>

<style scoped>
.recurrency-card {
  background-color: #fff;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  overflow: hidden;
  transition: all 0.15s;
  display: flex;
  flex-direction: column;
}

.recurrency-card:hover {
  border-color: #dee2e6;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}

.recurrency-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid #f1f3f5;
}

.recurrency-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  background-color: #f8f9fa;
  display: block;
}

.recurrency-avatar-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #e9ecef;
  color: #adb5bd;
  font-size: 1.2rem;
}

.recurrency-header-info {
  flex: 1;
  overflow: hidden;
  min-width: 0;
}

.recurrency-name {
  font-weight: 600;
  color: #212529;
  font-size: 0.95rem;
  line-height: 1.2;
  display: block;
}

.recurrency-name-link {
  text-decoration: none;
  transition: color 0.15s;
  color: #212529;
}

.recurrency-name-link:hover {
  color: #2563eb;
  text-decoration: underline;
}

.recurrency-price {
  font-size: 0.85rem;
  color: #495057;
  margin-top: 2px;
}

.recurrency-status-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  flex-shrink: 0;
}

.recurrency-status-badge.status-active {
  color: #0f5132;
  background-color: #d1e7dd;
}

.recurrency-status-badge.status-pending {
  color: #664d03;
  background-color: #fff3cd;
}

.recurrency-status-badge.status-cancelled {
  color: #842029;
  background-color: #f8d7da;
}

.recurrency-body {
  padding: 14px 16px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.recurrency-info-line {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  color: #495057;
}

.recurrency-info-line small {
  font-size: 0.75rem;
}

.recurrency-value-change {
  display: flex;
  align-items: center;
  font-size: 0.8rem;
  padding: 8px 10px;
  background-color: #fff7ed;
  color: #92400e;
  border: 1px solid #fed7aa;
  border-radius: 6px;
}

.recurrency-footer {
  padding: 0 16px 14px 16px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.recurrency-summary {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 12px 16px;
  border: 1px solid #e9ecef;
}
</style>
